import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useWtrSelector } from 'redux/hooks';
import classNames from 'classnames';
import { Link, useParams } from 'react-router-dom';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/ingredients/TextLink';
import { Typography } from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import isEqual from 'lodash/isEqual';

import env from 'env/env';
import MyWaitrose from 'components/ProductPod/MyWaitrose';

import { getCurrentPromotionPage } from 'redux/modules/search-and-browse/selectors/promotions';
import { getPromotionById } from 'redux/modules/entities/selectors/promotions';

import { trackProductPromotionClick } from 'analytics/product-promotion-click-tracking';
import { dataAttributesGenerator } from 'utils/data-attribute-generator';
import { getFeatureFlags } from 'utils/feature-flags';
import { promotionCodeMapper } from 'components/Product/Promotion/PromotionCodeMapper';
import PromotionDescription from './Description';
import { getPromotionStyles } from './get-promotion-styles';

import styles from './index.scss';

const promoRegex = /(([aA]dd) [1-9][0-9]* (for)){1}/;
const addRegex = /([aA]dd) /;
const ariaLabel = description =>
  promoRegex.test(description) ? description.replace(addRegex, '') : description;

const PromotionElement = memo(
  ({
    productId = '',
    promotionId = '',
    qualifier = '',
    showMyWaitrose = false,
    isSubPod = false,
    isTrolley = false,
  }) => {
    const promotion = useWtrSelector(state => getPromotionById(state, promotionId), isEqual) || {};

    const currentPromotionPage = useWtrSelector(
      state => getCurrentPromotionPage(state, promotion),
      isEqual,
    );

    const {
      hidden,
      myWaitrosePromotion,
      offerDisplayPrice,
      offerDisplayPriceQualifier,
      promotionDescription,
      promoPath = `/shop/offers/${promotionId}`,
      promotionTypeCode,
    } = promotion;

    const { mealDealId } = useParams();

    const promoCodeString = promotionCodeMapper(promotionTypeCode);

    const { backgroundColor, isLink, withChevron } = getPromotionStyles({
      promotionTypeCode,
      currentPromotionPage,
    });

    if (!promotionDescription || !!mealDealId) {
      return null;
    }

    if (hidden && !isTrolley) {
      return null;
    }

    const description = (
      <PromotionDescription
        {...{
          productId,
          promotion,
          promotionId,
          underlineText: !currentPromotionPage,
          noMargin: !!(showMyWaitrose || backgroundColor),
          backgroundColor,
          withChevron,
          isLink,
          isTrolley,
        }}
      />
    );

    const offerClasses = classNames(styles.offer, {
      [styles.withBackground]: backgroundColor,
      [styles.green]: backgroundColor === 'green',
      [styles.trolleyPage]: isTrolley,
      [styles.mwOffer]: myWaitrosePromotion && isSubPod,
    });

    let promoElement;

    if (isLink) {
      const dataAttributes = dataAttributesGenerator({
        actiontype: 'redirect',
        origincomponent: 'PromotionElement',
        shortdescription: `view products in ${promotionDescription} offer`,
        test: 'link-offer',
        'cs-override-id': promoCodeString ? `promo-text-${promoCodeString}` : undefined,
      });

      promoElement = (
        <TextLink
          underline="never"
          aria-label={ariaLabel(promotionDescription)}
          component={Link}
          {...dataAttributes}
          to={`${env.root}${promoPath}`}
          onClick={() => trackProductPromotionClick(promotionDescription)}
          rel={getFeatureFlags().browse_offersLinksWithRelNofollow ? 'nofollow' : undefined}
          className={classNames({ [styles.mwOfferLink]: showMyWaitrose })}
        >
          {showMyWaitrose && <MyWaitrose promotion={promotion} isSubPod={isSubPod} />}
          {description}
        </TextLink>
      );
    } else {
      promoElement = (
        <>
          {showMyWaitrose && <MyWaitrose promotion={promotion} isSubPod={isSubPod} />}
          {description}
        </>
      );
    }

    return (
      <div
        className={classNames(styles.offerWrapper, {
          [styles.green]: myWaitrosePromotion,
        })}
      >
        <div
          data-testid="offer-element"
          className={offerClasses}
          id={`${productId}_${promotionId}${qualifier}`}
        >
          {promoElement}
        </div>
        <div className={styles.offerDisplayPricesWrapper}>
          {offerDisplayPrice ? (
            <Typography
              className={styles.offerDisplayPrice}
              data-testid="offer-display-price"
              noMargins
              styleAs="sectionHeading"
              textStyle="heading-xl"
            >
              {offerDisplayPrice}
            </Typography>
          ) : null}
          {offerDisplayPriceQualifier ? (
            <Typography
              className={classNames(styles.pricePerUnit, { [styles.green]: myWaitrosePromotion })}
              data-testid="offer-display-price-qualifier"
              noMargins
            >
              {offerDisplayPriceQualifier}
            </Typography>
          ) : null}
        </div>
      </div>
    );
  },
);

PromotionElement.propTypes = {
  productId: PropTypes.string,
  promotionId: PropTypes.number,
  qualifier: PropTypes.string,
  searchType: PropTypes.string,
  showMyWaitrose: PropTypes.bool,
  isTrolley: PropTypes.bool,
};

PromotionElement.displayName = 'PromotionElement';

export default PromotionElement;
